import React, { useMemo, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import {
  LogOut, Palmtree, FlaskConical, LayoutDashboard, Users, IdCard, Files,
  CalendarDays, Settings, Menu as MenuIcon,
  UserCircle, ChevronRight, ClipboardCheck,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import NotificationsBell from '@/components/NotificationsBell';
import {
  hasCap, hasAnyCapUnder, isPureEmployee, canApproveFerie, canViewPersonale,
  canViewUsers, canViewTurni,
  canManageOwnAvailability,
} from '@/lib/capabilities';
import { useEffectiveMe } from '@/lib/effectiveMe';
import { roleDisplayLabel } from '@/lib/roleDisplayLabel';

/**
 * Capability-driven layout.
 *
 * The nav is composed from the user's `effective_capabilities` (fetched by
 * /api/me), NOT from a role code switch. This way:
 *   - A DIPENDENTE never sees admin sections (Personale, Team, admin Ferie).
 *   - Future modules stay compact until their routes and data sources exist.
 *   - CEO/TITOLARE keep the full currently-available admin UX.
 *
 * Backend still enforces every endpoint with 403 — this is UX, not security.
 */
export default function Layout() {
  const {
    effectiveRole, currentRoleId, devImpersonateRoleId, devImpersonatePersonaId,
    impersona, impersonaPersona, logout, ruoli, scope, aziende, locali, dipendenti,
  } = useApp();
  const { signOut, me: realMe } = useAuth();
  // `me` reflects DEV impersonation if active — every capability decision uses
  // this synthesized identity. The DEV picker itself stays gated on the REAL
  // account (so a non-CEO can't spawn a picker even if impersonation code path
  // is reached client-side).
  const me = useEffectiveMe();
  const navigate = useNavigate();
  const location = useLocation();
  // Turni mobile owns its own compact top bar → hide the global one on mobile.
  const isTurniPage = location.pathname === '/turni';
  // This page renders the verified server tenant context itself. The legacy
  // role-derived scope label would incorrectly say "Tutta la piattaforma".
  const isQrAdminPage = location.pathname === '/impostazioni/timbratura-qr';

  const employeeMode = isPureEmployee(me);
  const showDevTools = process.env.REACT_APP_SHOW_DEV_TOOLS !== 'false' && !!realMe?.is_global;
  const displayedRole = roleDisplayLabel({
    roleId: devImpersonateRoleId,
    personaId: devImpersonatePersonaId,
    roles: ruoli,
  });
  const isFocusedFoundation = ['/foundation', '/audit'].includes(location.pathname);

  const NAV = useMemo(() => {
    if (!me) return [];
    const items = [
      { to: '/foundation', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-foundation' },
    ];
    // Ferie: two shapes — admin console vs self-service — never both.
    if (canApproveFerie(me) || hasCap(me, 'ferie.balance.manage')) {
      items.push({ to: '/ferie', label: 'Ferie', icon: Palmtree, testid: 'nav-ferie' });
    } else if (hasCap(me, 'ferie.view') || hasCap(me, 'ferie.request')) {
      items.push({ to: '/ferie-mie', label: 'Le mie ferie', icon: Palmtree, testid: 'nav-ferie-mie' });
    }
    if (employeeMode && hasCap(me, 'self.view')) {
      items.push({ to: '/miei-documenti', label: 'Documenti', icon: Files, testid: 'nav-miei-documenti' });
    }
    if (canViewPersonale(me)) {
      items.push({ to: '/personale', label: 'Personale', icon: IdCard, testid: 'nav-personale' });
    }
    if (!employeeMode && (hasCap(me, 'employee.view') || hasCap(me, 'personale.view') || hasAnyCapUnder(me, 'personale.documents'))) {
      items.push({ to: '/documenti-personale', label: 'Documenti del personale', icon: Files, testid: 'nav-documenti-personale' });
    }
    if (hasCap(me, 'attendance.view') || (employeeMode && hasCap(me, 'attendance.self'))) {
      items.push({ to: '/presenze', label: 'Presenze', icon: ClipboardCheck, testid: 'nav-presenze' });
    }
    if (isPureEmployee(me) && (canViewTurni(me) || canManageOwnAvailability(me))) {
      items.push({ to: '/turni', label: 'I miei turni', icon: CalendarDays, testid: 'nav-turni' });
    } else if (canViewTurni(me)) {
      items.push({ to: '/turni', label: 'Turni', icon: CalendarDays, testid: 'nav-turni' });
    }
    if (canViewUsers(me)) {
      items.push({ to: '/foundation/users', label: 'Gestione Titolari', icon: Users, testid: 'nav-users' });
    }
    if (hasAnyCapUnder(me, 'department') || hasAnyCapUnder(me, 'ferie.config')) {
      items.push({ to: '/impostazioni', label: 'Impostazioni', icon: Settings, testid: 'nav-impostazioni' });
    }
    if (showDevTools) {
      // Developer-only navigation remains intentionally hidden from users.
    }
    return items;
  }, [me, employeeMode, showDevTools]);

  const [menuOpen, setMenuOpen] = useState(false);

  // Mobile: pin 4 primary tabs (Dashboard, Ferie, Personale, Turni) then a "Menu"
  // slot for everything else — capability-driven; hidden sections don't appear.
  const PRIMARY_PATHS = ['/foundation', '/ferie', '/ferie-mie', '/miei-documenti', '/personale', '/turni', '/presenze'];
  const primaryMobile = NAV.filter(n => PRIMARY_PATHS.includes(n.to)).slice(0, 4);
  const primaryMobilePaths = new Set(primaryMobile.map((item) => item.to));
  const menuItems = NAV.filter(n => !primaryMobilePaths.has(n.to));

  // Menu sections for mobile drawer (capability-driven)
  const menuSections = useMemo(() => {
    if (!me) return [];
    const sections = [];
    // IMPOSTAZIONI — one entry; available sections are reached inside Settings.
    if (hasAnyCapUnder(me, 'department') || hasAnyCapUnder(me, 'ferie.config')) {
      sections.push({
        title: 'Impostazioni',
        items: [{ to: '/impostazioni', label: 'Impostazioni', icon: Settings, testid: 'menu-impostazioni' }],
      });
    }
    // AMMINISTRAZIONE
    const admin = [];
    if (canViewUsers(me)) admin.push({ to: '/foundation/users', label: 'Gestione Titolari', icon: Users, testid: 'menu-users' });
    if (!employeeMode && (hasCap(me, 'employee.view') || hasCap(me, 'personale.view') || hasAnyCapUnder(me, 'personale.documents')))
      admin.push({ to: '/documenti-personale', label: 'Documenti del personale', icon: Files, testid: 'menu-documenti-personale' });
    if (admin.length) sections.push({ title: 'Amministrazione', items: admin });
    // ACCOUNT
    const account = [];
    if (employeeMode && (canViewTurni(me) || canManageOwnAvailability(me)))
      account.push({ to: '/turni', label: 'I miei turni', icon: CalendarDays, testid: 'menu-miei-turni' });
    if (employeeMode && hasCap(me, 'self.view'))
      account.push({ to: '/miei-documenti', label: 'Documenti', icon: Files, testid: 'menu-miei-documenti' });
    account.push({ to: '__logout__', label: 'Esci', icon: LogOut, testid: 'menu-logout' });
    sections.push({ title: 'Account', items: account });
    return sections;
  }, [me, employeeMode]);

  if (!currentRoleId) return null;

  const doLogout = async () => { await signOut(); logout(); navigate('/'); };

  // Scope label is admin-oriented ("Tutta la piattaforma", "Locale X"...) and
  // meaningless for a pure employee — hide it entirely in employee-mode.
  const scopeLabel = () => {
    if (effectiveRole?.id === 'ceo') return 'Tutta la piattaforma';
    if (effectiveRole?.id === 'titolare') return 'Tutte le aziende';
    if (scope.dipendenteId) return 'Solo il tuo profilo';
    if (scope.localeId)  return locali.find(l => l.id === scope.localeId)?.nome;
    if (scope.aziendaId) return aziende.find(a => a.id === scope.aziendaId)?.nome;
    return '—';
  };

  const bottomNav = primaryMobile;
  const showMenuSlot = menuItems.length > 0 || menuSections.some(s => s.items.length > 0);

  // The approved Titolare dashboard owns its compact chrome and mobile
  // navigation. Keep the shared shell for employee self-service and every
  // other route so the dashboard does not render a second header/sidebar.
  if (isFocusedFoundation) {
    return (
      <div className="min-h-screen bg-app-background">
        {showDevTools && employeeMode && displayedRole.startsWith('DEV: ') && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900" data-testid="employee-role-preview">
            <div><strong>{displayedRole}</strong><p className="text-xs">Preview visuale e di navigazione, non verifica dei permessi server.</p></div>
            <button type="button" className="rounded-lg border border-amber-300 px-3 py-2 font-semibold" onClick={() => impersona(null)} data-testid="employee-preview-exit">Torna a CEO</button>
          </div>
        )}
        <Outlet context={{ openMobileMenu: () => {}, scopeLabel: scopeLabel() }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app-background flex">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex flex-col w-64 border-r border-stone-200 bg-white sticky top-0 h-screen">
        <div className="p-5 border-b border-stone-200">
          <img src="/brand/restaurantos-logo-horizontal.svg" alt="RestaurantOS" className="h-10 w-auto" data-testid="brand-logo-sidebar" />
        </div>
        <nav className="p-3 flex-1">
          {NAV.map(item => (
            <NavLink key={item.to} to={item.to} data-testid={item.testid}
              className={({isActive}) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1 text-sm font-medium transition-colors ${
                  isActive ? 'bg-emerald-50 text-brand-green-hover' : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                }`
              }>
              <item.icon className="w-4 h-4" strokeWidth={2.2} /> {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-stone-200 space-y-2">
          {!employeeMode && (
            <div className="px-3 py-2 rounded-xl bg-stone-50">
              <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Ruolo attivo</div>
              <div className="text-sm font-medium text-stone-800">{effectiveRole?.nome}</div>
              <div className="text-[11px] text-stone-500 mt-0.5">{scopeLabel()}</div>
            </div>
          )}
          {employeeMode && (
            <div className="px-3 py-2 rounded-xl bg-emerald-50">
              <div className="text-[10px] uppercase tracking-widest text-brand-green font-bold">Area personale</div>
              <div className="text-sm font-medium text-brand-navy truncate">{me?.full_name || me?.email}</div>
            </div>
          )}
          <button onClick={doLogout} data-testid="logout-btn-desktop"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-stone-600 hover:bg-stone-50 hover:text-stone-900 transition-colors">
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className={`sticky top-0 z-20 bg-white/85 backdrop-blur-md border-b border-stone-200 ${isTurniPage ? 'hidden md:block' : ''}`}>
          <div className="ros-layout-topbar-content px-4 md:px-8 py-3.5 flex items-center justify-between gap-3">
            <div className="md:hidden flex items-center gap-2">
              <img src="/brand/restaurantos-logo-symbol.svg" alt="RestaurantOS" className="w-8 h-8" data-testid="brand-logo-mobile" />
              <div className="font-display font-semibold text-stone-900 text-sm tracking-tight">RestaurantOS</div>
            </div>
            <div className="flex-1 flex items-center justify-end gap-2">
              {showDevTools && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="dev-impersona"
                      className="flex items-center gap-2 px-3 py-2 rounded-full bg-amber-50 border border-amber-200 hover:border-amber-300 transition-colors">
                      <span className="w-2 h-2 rounded-full bg-amber-500"/>
                      <span className="text-xs font-semibold text-amber-900">
                        {displayedRole}
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 max-h-[70vh] overflow-auto">
                    <DropdownMenuLabel className="text-xs">Simula ruolo</DropdownMenuLabel>
                    <p className="px-2 py-1 text-xs text-stone-500">Preview visuale e di navigazione, non verifica dei permessi server.</p>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="impersona-none" onClick={() => impersona(null)}>Torna a CEO</DropdownMenuItem>
                    {ruoli.filter(r => r.id !== 'ceo').map(r => (
                      <DropdownMenuItem key={r.id} data-testid={`impersona-${r.id}`} onClick={() => impersona(r.id)}>
                        {r.nome}{r.id === 'dipendente' ? ' · simulazione generica' : ''}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger data-testid="dev-simula-persona-trigger" className="text-xs">
                        Simula persona reale…
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent className="w-64 max-h-[60vh] overflow-auto">
                          <DropdownMenuLabel className="text-[11px] text-stone-500">
                            Read-only · nessuna scrittura DB
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {[...dipendenti]
                            .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
                            .map(d => (
                              <DropdownMenuItem key={d.id}
                                data-testid={`dev-simula-persona-${d.id}`}
                                onClick={() => impersonaPersona(d.id)}
                                className="text-xs">
                                {d.displayName}
                                <span className="ml-auto text-[10px] text-stone-400 font-mono">{d.id}</span>
                              </DropdownMenuItem>
                            ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {!employeeMode && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-50 border border-emerald-100">
                  <span className="w-2 h-2 rounded-full bg-emerald-600"/>
                  <span className="text-xs font-medium text-brand-green-hover">{effectiveRole?.nome}</span>
                </div>
              )}

              <NotificationsBell />

              <button onClick={doLogout} data-testid="logout-btn"
                className="md:hidden p-2 rounded-full bg-white border border-stone-200 text-stone-600" aria-label="Esci">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!employeeMode && !isTurniPage && !isQrAdminPage && (
            <div className="px-4 md:px-8 pb-3 text-xs text-stone-500">
              Scope: <span className="text-stone-800 font-medium">{scopeLabel()}</span>
            </div>
          )}
        </header>

        <main className={`flex-1 pb-safe ${isTurniPage ? 'px-0 py-0 md:px-8 md:py-8' : 'px-4 md:px-8 py-6 md:py-8'}`}>
          {devImpersonateRoleId && (
            <div
              className="mx-auto mb-4 max-w-5xl rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs font-semibold text-amber-900"
              data-testid="dev-simulation-banner"
              role="status"
            >
              Modalità simulazione · dati personali reali non caricati · non è un account reale
            </div>
          )}
          <Outlet context={{ openMobileMenu: () => setMenuOpen(true), scopeLabel: scopeLabel() }} />
        </main>
      </div>

      {/* Bottom nav mobile — max 5 slots (4 primary + Menu). Capability-driven. */}
      <nav className="ros-bottom-nav md:hidden fixed bottom-0 left-0 right-0 z-30">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${bottomNav.length + (showMenuSlot ? 1 : 0)}, minmax(0, 1fr))` }}>
          {bottomNav.map(item => (
            <NavLink key={item.to} to={item.to} data-testid={`${item.testid}-mobile`}
              className={({isActive}) =>
                   `flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
                  isActive ? 'text-brand-green' : 'text-stone-500'
                }`
              }>
              <item.icon className="w-5 h-5" strokeWidth={2.2}/>
              {item.label}
            </NavLink>
          ))}
          {showMenuSlot && (
            <button data-testid="nav-menu-mobile" onClick={() => setMenuOpen(true)}
               className={`flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
                menuOpen ? 'text-brand-green' : 'text-stone-500'
              }`}>
              <MenuIcon className="w-5 h-5" strokeWidth={2.2}/>
              Menu
            </button>
          )}
        </div>
      </nav>

      {/* Mobile Menu drawer */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="right" className="w-[85vw] max-w-sm p-0 flex flex-col" data-testid="mobile-menu-sheet">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-stone-100">
            <SheetTitle className="font-display text-brand-navy text-xl">Menu</SheetTitle>
            <div className="text-[11px] text-stone-500">
              {employeeMode ? me?.full_name || me?.email : `${effectiveRole?.nome || ''} · ${scopeLabel()}`}
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto py-3">
            {menuSections.map(section => (
              <div key={section.title} className="mb-4">
                <div className="px-5 py-1.5 text-[10px] uppercase tracking-widest text-stone-400 font-bold">
                  {section.title}
                </div>
                <ul>
                  {section.items.map(item => (
                    <li key={item.to}>
                      {item.to === '__logout__' ? (
                        <button data-testid={item.testid} onClick={() => { setMenuOpen(false); doLogout(); }}
                          className="w-full flex items-center gap-3 px-5 py-3 text-sm font-medium text-red-600 hover:bg-red-50">
                          <item.icon className="w-4 h-4" /> {item.label}
                        </button>
                      ) : (
                        <NavLink to={item.to} data-testid={item.testid} onClick={() => setMenuOpen(false)}
                          className={({isActive}) =>
                            `flex items-center gap-3 px-5 py-3 text-sm font-medium transition-colors ${
                              isActive ? 'bg-emerald-50 text-brand-green-hover' : 'text-stone-800 hover:bg-stone-50'
                            }`
                          }>
                          <item.icon className="w-4 h-4" /> <span className="flex-1">{item.label}</span>
                          <ChevronRight className="w-4 h-4 text-stone-300"/>
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
